import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import { backgroundGrey4 } from 'lib/css/colors';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import Select from 'react-select/creatable';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

import { arraysAreEqual } from 'lib/utils/arrayUtils';
import DropZone from 'components/shared/Dropzone';
import { handleCopyToClipboard } from 'lib/utils/stringUtils';
import ImagesDisplayModal from 'components/settings/support_tickets/ImagesDisplayModal';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import pointerOnHover from 'lib/css/pointerOnHover';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import {
  CURRENT_USER,
  SUPPORT_TICKET_GET_BY_ID,
  SUPPORT_TICKETS_UPDATE,
  USERS_GET_FOR_DROPDOWN,
} from 'lib/networking/endpoints';
import SupportTicketCommentBox from 'components/settings/support_tickets/SupportTicketCommentBox';
import {
  SupportTicketOriginLabels,
  SupportTicketStatusOptions,
  SupportTicketEntityType,
  SupportTicketEntityTypeLabels,
} from 'lib/enums/SupportTicketEnums';
import SwalDialog from 'lib/utils/toast';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
function SupportTicketsModal({ ticketId, onHide, refetch }) {
  const { data: { ticket } = {}, loading: loading } = useGet(SUPPORT_TICKET_GET_BY_ID, {
    id: ticketId,
  });

  const images =
    ticket && ticket?.details?.attachments?.length > 0
      ? ticket.details.attachments.map(imageUrl => ({
          url: imageUrl,
        }))
      : [];

  const initialState = useMemo(
    () => ({
      status: ticket?.status,
      assignedTo: {
        label: ticket?.assigned_to?.name || ticket?.meta_data?.assigned_to_name || null,
        value: ticket?.assigned_to?.id || ticket?.meta_data?.assigned_to_id || null,
      },
      comments: ticket?.comments || [],
    }),
    [ticket],
  );

  const [updatedState, setUpdatedState] = useState(initialState);
  const [newComment, setNewComment] = useState({ name: '', comment: '', attachments: [] });
  const [displayCommentOptions, setDisplayCommentOptions] = useState(false);
  const [showAttachmentsModal, setShowAttachmentsModal] = useState(false);

  const { data: { user } = {} } = useGet(CURRENT_USER);

  useEffect(() => {
    setUpdatedState(initialState);
  }, [initialState]);

  const { postData: updateTicket, loading: updatingTicket } = usePost(
    SUPPORT_TICKETS_UPDATE,
    () => {
      SwalDialog('success', `Successfully updated the ticket.`, 'Success', 'center', () => {
        refetch();
        onHide();
      });
    },
    () => {
      SwalDialog('error', 'Error occurred while updating the ticket', 'Error', 'center');
    },
  );

  const hasChanges =
    updatedState.status !== initialState.status ||
    updatedState.assignedTo?.value !== initialState.assignedTo.value ||
    !arraysAreEqual(updatedState.comments, initialState.comments);

  const handleSaveChanges = () => {
    const updatedFields = {};
    if (updatedState.status !== initialState.status) updatedFields.status = updatedState.status;
    if (updatedState.assignedTo?.value !== initialState.assignedTo.value)
      updatedFields.assigned_to_id = updatedState.assignedTo.value;
    if (!arraysAreEqual(updatedState.comments, initialState.comments))
      updatedFields.comments = updatedState.comments;

    updateTicket({
      ticket_id: ticket?.id,
      ...updatedFields,
    });
  };

  const sku =
    ticket?.vendor_product?.vendor_sku ||
    ticket?.manufacturer_product?.manufacturer_sku ||
    ticket?.meta_data?.vendor_product?.vendor_sku ||
    ticket?.meta_data?.manufacturer_product?.manufacturer_sku ||
    '';

  const handleAddNewComment = e => {
    const comment = {
      commented_by: user?.name,
      comment: newComment.comment,
      timestamp: Math.floor(new Date().getTime() / 1000),
      attachments: newComment.attachments,
    };
    setUpdatedState(prev => ({
      ...prev,
      comments: [...prev.comments, comment],
    }));
    setDisplayCommentOptions(false);
    setNewComment({ ...newComment, comment: '', attachments: [] });
  };

  const handleEditComment = (index, updatedComment) => {
    setUpdatedState(prev => {
      const newComments = [...prev.comments];
      newComments[newComments.length - 1 - index] = {
        ...newComments[newComments.length - 1 - index],
        comment: updatedComment,
      };
      return { ...prev, comments: newComments };
    });
  };

  return (
    <>
      <Modal show={true} onHide={onHide} size="xl" className="lato" centered>
        {(updatingTicket || loading) && (
          <div className={css(styles.overlay)}>
            <LoadingSpinner />
          </div>
        )}
        <Modal.Header closeButton>
          <div className={css(styles.headerContent)}>
            <Modal.Title>
              <strong>FSA - {ticket?.id}</strong>
            </Modal.Title>
            <OverlayTrigger overlay={<Tooltip>Copy ticket URL to clipboard</Tooltip>}>
              <FontAwesomeIcon
                icon={faCopy}
                className={classNames(pointerOnHover, 'ml-3')}
                onClick={() =>
                  handleCopyToClipboard(`${window.location.origin}/support-tickets/${ticket?.id}`)
                }
              />
            </OverlayTrigger>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col className={css(styles.leftCol)}>
              <Row className={css(styles.task)}>
                <strong>{ticket?.task}</strong>
              </Row>
              <Row className={classNames('ml-2', css(styles.descriptionRow))}>
                <strong className={classNames(css(styles.subSectionsTitle), 'text-muted')}>
                  Description
                </strong>
                <div className={css(styles.subSectionDetails)}>
                  {ticket?.details.description.split('\n').map((line, index) => (
                    <div key={index}>{line}</div>
                  ))}
                </div>
              </Row>
              {images.length > 0 ? (
                <Row className="ml-2">
                  <Button variant="outline-primary" onClick={() => setShowAttachmentsModal(true)}>
                    View {images.length} Attachments
                  </Button>
                </Row>
              ) : null}
            </Col>
            <Col>
              {ticket?.entity_type === SupportTicketEntityType.BRAND ? (
                <>
                  <strong className={classNames(css(styles.subSectionsTitle), 'text-muted')}>
                    Brand
                  </strong>
                  <div
                    className={css(styles.clickableText)}
                    onClick={
                      ticket?.brand
                        ? () =>
                            window.open(
                              `/enrich-products/brand-details/${ticket?.brand?.id}`,
                              '_blank',
                            )
                        : null
                    }
                  >
                    {ticket?.brand?.name || ticket?.meta_data?.brand?.name}
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <strong className={classNames(css(styles.subSectionsTitle), 'text-muted')}>
                      SKU
                    </strong>
                    <OverlayTrigger overlay={<Tooltip>Copy SKU to clipboard</Tooltip>}>
                      <FontAwesomeIcon
                        icon={faCopy}
                        className={classNames(pointerOnHover, 'ml-2')}
                        onClick={() => handleCopyToClipboard(sku)}
                      />
                    </OverlayTrigger>
                  </div>
                  <div
                    className={css(styles.clickableText)}
                    onClick={() => {
                      const productType = ticket?.vendor_product ? 'distributor' : 'manufacturer';
                      const product = ticket?.vendor_product || ticket?.manufacturer_product;
                      const productId = product?.id;
                      const globalProductId = product?.global_product?.id;
                      window.open(
                        `/global-product-detail/${globalProductId}/${productId}/${productType}`,
                        '_blank',
                      );
                    }}
                  >
                    {sku}
                  </div>
                  <strong className={classNames(css(styles.subSectionsTitle), 'text-muted')}>
                    Item Name
                  </strong>
                  <div className={css(styles.subSectionDetails)}>
                    {ticket?.vendor_product?.name ||
                      ticket?.manufacturer_product?.name ||
                      ticket?.meta_data?.vendor_product?.name ||
                      ticket?.meta_data?.manufacturer_product?.name ||
                      ''}
                  </div>
                  <strong className={classNames(css(styles.subSectionsTitle), 'text-muted')}>
                    Product entity - {SupportTicketEntityTypeLabels[ticket?.entity_type]}
                  </strong>
                  <div className={css(styles.subSectionDetails)}>
                    {ticket?.global_vendor?.name ||
                      ticket?.global_manufacturer?.name ||
                      ticket?.meta_data?.global_vendor?.name ||
                      ticket?.meta_data?.global_manufacturer?.name ||
                      ''}
                  </div>
                </>
              )}
              <strong className={classNames(css(styles.subSectionsTitle), 'text-muted')}>
                Reported By - {SupportTicketOriginLabels[ticket?.origin]}
              </strong>
              <div className={css(styles.subSectionDetails)}>{ticket?.reported_by}</div>
              <Form.Group>
                <Form.Label className={classNames(css(styles.subSectionsTitle), 'text-muted')}>
                  Assigned To
                </Form.Label>
                <SearchableScrollableSelect
                  initOption={updatedState.assignedTo}
                  onChange={option =>
                    setUpdatedState(prevState => ({ ...prevState, assignedTo: option || null }))
                  }
                  fetchUrl={USERS_GET_FOR_DROPDOWN}
                  optionMapper={assignedTo => ({ label: assignedTo.name, value: assignedTo.id })}
                  placeholder="Select assignedTo"
                  autoFocus={false}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className={classNames(css(styles.subSectionsTitle), 'text-muted')}>
                  Status
                </Form.Label>
                <Select
                  className={css(styles.subSectionDetails)}
                  value={
                    SupportTicketStatusOptions.find(
                      option => option.value === updatedState.status,
                    ) || null
                  }
                  options={SupportTicketStatusOptions}
                  onChange={option =>
                    setUpdatedState(prevState => ({ ...prevState, status: option.value }))
                  }
                />
              </Form.Group>
              {ticket?.callback_url ? (
                <>
                  <strong className={classNames(css(styles.subSectionsTitle), 'text-muted')}>
                    Comments
                  </strong>
                  <Form.Group>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={newComment.comment}
                      placeholder="Add your comment here"
                      onChange={e => {
                        setNewComment(prev => ({ ...prev, comment: e.target.value }));
                      }}
                      className="mb-3"
                      style={{ overflowY: 'auto' }}
                      onFocus={() => setDisplayCommentOptions(true)}
                    />
                  </Form.Group>
                  {displayCommentOptions && (
                    <>
                      <Button className="mr-2 mb-2" onClick={() => handleAddNewComment()}>
                        Add new comment
                      </Button>
                      <Button
                        onClick={() => {
                          setDisplayCommentOptions(false);
                          setNewComment({ ...newComment, comment: '', attachments: [] });
                        }}
                        className="mr-2 mb-2"
                      >
                        Cancel
                      </Button>
                      <DropZone
                        text="Drop your file here to upload or click to browse"
                        onUploadSuccess={newUrls =>
                          setNewComment({ ...newComment, attachments: newUrls })
                        }
                        fileUrls={newComment.attachments}
                        isImage={true}
                        isPublic={true}
                        isMulti={true}
                        isDownloadable={true}
                      />
                    </>
                  )}
                  <div className={css(styles.commentSection)}>
                    {updatedState?.comments
                      ?.slice()
                      .reverse()
                      .map((comment, index) => (
                        <SupportTicketCommentBox
                          key={index}
                          comment={comment}
                          user={user}
                          origin={ticket?.origin}
                          onEditComment={updatedComment => handleEditComment(index, updatedComment)}
                          canEdit={comment.commented_by === user?.name}
                        />
                      ))}
                  </div>
                </>
              ) : null}
              <Button variant="primary" onClick={handleSaveChanges} disabled={!hasChanges}>
                Save
              </Button>
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
      {showAttachmentsModal && (
        <ImagesDisplayModal
          images={images}
          onHide={() => setShowAttachmentsModal(false)}
          origin={ticket?.origin}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  task: {
    fontSize: '1.8rem',
    marginBottom: '1.2rem',
    borderBottom: '3px solid ' + backgroundGrey4,
  },
  subSectionsTitle: {
    fontSize: '1.2rem',
    marginBottom: '1.2rem',
  },
  subSectionDetails: {
    marginBottom: '1rem',
  },
  leftCol: {
    paddingLeft: '1.9rem',
    paddingRight: '1rem',
    borderRight: '3px solid ' + backgroundGrey4,
  },
  descriptionRow: {
    display: 'flex',
    flexDirection: 'column',
  },
  clickableText: {
    color: 'blue',
    textDecoration: 'underline',
    cursor: 'pointer',
    marginBottom: '1rem',
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    zIndex: 1050,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  commentSection: {
    maxHeight: '300px',
    overflowY: 'auto',
    padding: '10px',
    border: '2px solid ' + backgroundGrey4,
    borderRadius: '4px',
    marginTop: '1.5rem',
    marginBottom: '1.5rem',
  },
});

SupportTicketsModal.propTypes = {
  ticketId: PropTypes.number,
  onHide: PropTypes.func,
  refetch: PropTypes.func,
};

export default SupportTicketsModal;
